import { useId, useState } from 'react';
import { Link } from 'react-router';

import { instantForTransactionDate, todayInputValue } from '../../../shared/lib/dates.js';
import { toAppError } from '../../../shared/lib/errors.js';
import forms from '../../../shared/styles/forms.module.css';
import { EmptyState } from '../../../shared/ui/EmptyState.js';
import {
  buildIncomePayload,
  postIncome,
  updateEntry,
  type QuickAddOptions,
  type SubjectContext,
} from '../data/quickAddRepository.js';
import { AmountCard } from './AmountCard.js';
import { ChipRow } from './ChipRow.js';
import { DatePicker } from './DatePicker.js';
import { missingFieldsMessage } from '../model/missingFields.js';
import { MissingHint } from './MissingHint.js';
import styles from './QuickAddControls.module.css';

export type IncomeFormInitial = Readonly<{
  amount: number;
  incomeSourceId: string;
  destinationAccountId: string;
  date: string;
  note: string;
}>;

/**
 * "Thu nhập" — amount → nguồn thu → tài khoản nhận → Lưu (spec v2 22.1).
 *
 * Choosing a source moves the destination to the account that money usually lands in, so
 * salary day is two taps. Doubles as the edit form for an existing income entry.
 */
export function IncomeForm({
  options,
  subjects,
  timeZone,
  entryId,
  initial,
  onSaved,
  onCancel,
}: {
  options: QuickAddOptions;
  subjects: SubjectContext;
  timeZone: string;
  entryId?: string;
  initial?: IncomeFormInitial;
  onSaved: (message: string) => void;
  onCancel?: () => void;
}) {
  const noteId = useId();

  const firstSource = options.incomeSources[0];
  const [amount, setAmount] = useState<number | null>(initial?.amount ?? null);
  const [incomeSourceId, setIncomeSourceId] = useState(
    initial?.incomeSourceId ?? firstSource?.id ?? '',
  );
  const [destination, setDestination] = useState(
    initial?.destinationAccountId ??
      firstSource?.defaultDestinationAccountId ??
      options.defaultAccountId ??
      '',
  );
  const [date, setDate] = useState(initial?.date ?? (() => todayInputValue(timeZone)));
  const [note, setNote] = useState(initial?.note ?? '');

  const [message, setMessage] = useState<string | null>(null);
  const [isSaving, setSaving] = useState(false);

  if (options.incomeSources.length === 0) {
    return (
      <EmptyState
        icon="💰"
        title="Chưa có nguồn thu nào"
        message="Thêm nguồn thu (lương, hoàn thuế…) rồi quay lại ghi khoản thu."
      >
        <Link to="/nguon-thu">Thêm nguồn thu</Link>
      </EmptyState>
    );
  }

  const canSave = amount !== null && amount > 0 && incomeSourceId !== '' && destination !== '';

  /** Picking a source moves the destination to where that money usually lands. */
  function chooseSource(nextId: string) {
    setIncomeSourceId(nextId);
    const next = options.incomeSources.find((item) => item.id === nextId);
    if (next?.defaultDestinationAccountId) {
      setDestination(next.defaultDestinationAccountId);
    }
  }

  async function handleSubmit() {
    if (!canSave || amount === null) return;

    setSaving(true);
    setMessage(null);
    try {
      const draft = {
        amount,
        incomeSourceId,
        destinationAccountId: destination,
        occurredAt: instantForTransactionDate(date, timeZone),
        ...(note.trim() === '' ? {} : { note: note.trim() }),
      };

      if (entryId === undefined) {
        await postIncome(options, subjects, draft);
        onSaved('Đã lưu khoản thu.');
      } else {
        await updateEntry(entryId, buildIncomePayload(options, subjects, draft));
        onSaved('Đã cập nhật khoản thu.');
      }
    } catch (error) {
      setMessage(toAppError(error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <form
      className={styles.form}
      onSubmit={(event) => {
        event.preventDefault();
        void handleSubmit();
      }}
      noValidate
    >
      <AmountCard value={amount} onChange={setAmount} autoFocus={entryId === undefined} />

      <ChipRow
        label="Nguồn thu"
        items={options.incomeSources.map((source) => ({ id: source.id, label: source.name }))}
        selectedId={incomeSourceId}
        onSelect={chooseSource}
      />

      <ChipRow
        label="Tiền về tài khoản"
        items={options.accounts.map((account) => ({
          id: account.id,
          label: account.name,
          accountType: account.accountType,
        }))}
        selectedId={destination}
        onSelect={setDestination}
        emptyMessage="Chưa có tài khoản nào — thêm ở màn Tài sản."
      />

      <DatePicker value={date} onChange={setDate} timeZone={timeZone} />

      <details className={forms.advanced}>
        <summary className={forms.summary}>Chi tiết</summary>

        <div className={forms.field}>
          <label htmlFor={noteId}>Ghi chú</label>
          <input
            id={noteId}
            className={forms.input}
            type="text"
            value={note}
            autoComplete="off"
            onChange={(event) => setNote(event.target.value)}
          />
        </div>
      </details>

      {message ? (
        <p className={forms.error} role="alert">
          {message}
        </p>
      ) : null}

      <div className={styles.footer}>
        {onCancel ? (
          <button type="button" className={forms.secondary} onClick={onCancel}>
            Hủy
          </button>
        ) : null}
        <button type="submit" className={styles.save} disabled={!canSave || isSaving}>
          {isSaving ? 'Đang lưu…' : entryId === undefined ? 'Lưu giao dịch' : 'Cập nhật'}
        </button>
      </div>
      <MissingHint
        message={missingFieldsMessage([
          (amount === null || amount <= 0) && 'số tiền',
          incomeSourceId === '' && 'nguồn thu',
          destination === '' && 'tài khoản nhận',
        ])}
      />
    </form>
  );
}
